import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs'
import { IProps as IPlanColumnGroupLayoutProps } from 'marketing-site/src/library/components/PlanColumnGroupLayout'
import { IProps as ISolutionWithTiersProps } from 'marketing-site/src/library/components/SolutionWithTiers'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { resolve } from 'styled-jsx/css'
import { PricingPageSolutionTabsHelp } from '../../elements/PricingPageSolutionTabsHelp'
import { Text } from '../../elements/Text'
import {
  AllSystemColors,
  AnalyticsEvent,
  Color,
  ExpandedThemeColor,
  mq,
  RouterWithQueryParams,
  useMediaQuery,
} from '../../utils'
import { PlanColumnGroupLayout } from '../PlanColumnGroupLayout'
import { SolutionWithTiers } from '../SolutionWithTiers'
import { IProps } from './index'
import { useMobileMenuBuilder } from './MobileMenuBuilder'
import styles from './styles.scss'

function getTabsStyles(numberOfSolutions: number) {
  return resolve`
    {
      display: grid;
      grid-template-columns:${' 1fr'.repeat(numberOfSolutions)};
      font-weight: bold;
      justify-content: center;
      max-width: ${numberOfSolutions == 2 ? 800 : 1256}px;
      margin: 0 auto;
      padding: 0 5%;
      margin-top: 80px;
    }
  `
}

function getTabStyles(numberOfSolutions: number) {
  return resolve`
    {
      padding: 10px;
      border: 2px solid ${Color.Stone};
      border-left: none;
      border-bottom: none;
      text-align: center;
      height: 60px;
    }

    :hover {
      cursor: pointer;
    }

    :focus {
      outline: none;
    }

    :first-child {
      margin-right: 20px;
      border-left: 2px solid ${Color.Stone};
      padding-left: 8px;
    }

    :last-child {
      border-top-right-radius: ${numberOfSolutions === 2 ? 0 : 4}px;
    }

    :nth-child(2) {
      border-left: 2px solid ${Color.Stone};
      padding-left: 8px;
      border-top-left-radius: ${numberOfSolutions === 2 ? 0 : 4}px;
    }

    :not([data-selected]) {
      border-top: 2px solid ${Color.Stone} !important;
    }

    [data-selected] {
      background-color: ${Color.Stone};
      border-top-width: 10px;
      border-left: none;
      border-right: none;
      padding-top: 2px;
      padding-left: 9px;
      margin-left: -1px;
    }

    ::-moz-focus-inner {
      border: 0;
    }
  `
}

function getTabPanelStyles() {
  return resolve`
    {
     background-color: ${Color.Stone};
    }
  `
}

export const TabbedSolutions = ({ solutions, showHelp, helpText }: IProps) => {
  const tabsStyles = getTabsStyles(solutions.length)
  const tabStyles = getTabStyles(solutions.length)
  const tabPanelStyles = getTabPanelStyles()

  const router = new RouterWithQueryParams(useRouter())
  const mobileMenuBuilder = useMobileMenuBuilder()

  const [tabIndex, setTabIndex] = useState(0)
  const selectedSolution = solutions[tabIndex]

  const isMobile = useMediaQuery(`(${mq.mobile})`)

  useEffect(() => {
    const { tab } = router.queryParams()
    if (tab) {
      setTabIndex(parseInt(tab as string, 10))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setTabAndUrl = function (index: number): void {
    setTabIndex(index)
    const path = router.getCurrentPath()

    const newSolution = solutions[index]
    const object =
      (newSolution as ISolutionWithTiersProps).eventContext ||
      (newSolution as IPlanColumnGroupLayoutProps).tabEventContext ||
      ''
    new AnalyticsEvent({
      action: 'clicked',
      object,
      place: 'pricing_page',
      context: 'pricing_page',
    }).send()

    const queryParamString =
      index === 0 ? router.removeQueryParam('tab') : router.updateQueryParam('tab', `${index}`)
    router.updateUrl(`${path}?${queryParamString}`)
  }

  const handleDropdownSelection = function (index: number): void {
    setTabAndUrl(index)
  }

  function isSolutionWithTiers(
    solution: IPlanColumnGroupLayoutProps | ISolutionWithTiersProps,
  ): solution is ISolutionWithTiersProps {
    return (solution as ISolutionWithTiersProps).tiers !== undefined
  }

  function tabHeading(solution: IPlanColumnGroupLayoutProps | ISolutionWithTiersProps): string {
    return (
      (solution as IPlanColumnGroupLayoutProps).tabHeading ||
      (solution as ISolutionWithTiersProps).heading
    )
  }

  function tabBorderColor(
    solution: IPlanColumnGroupLayoutProps | ISolutionWithTiersProps,
  ): ExpandedThemeColor | AllSystemColors {
    return (
      (solution as IPlanColumnGroupLayoutProps).tabColor ||
      (solution as ISolutionWithTiersProps).color
    )
  }

  return (
    <div>
      {showHelp && (
        <div className="tabbed-solutions__help">
          <PricingPageSolutionTabsHelp
            allInOneHelpText={helpText && helpText.allInOneHelpText}
            otherSolutionsHelpText={helpText && helpText.otherSolutionsHelpText}
          />
        </div>
      )}
      {isMobile ? (
        <div className="solutions-mobile">
          {handleDropdownSelection}
          {mobileMenuBuilder.build(
            { solutions, showHelp, helpText },
            handleDropdownSelection,
            tabHeading(selectedSolution),
          )}
          {isSolutionWithTiers(selectedSolution) ? (
            <SolutionWithTiers {...selectedSolution} />
          ) : (
            <PlanColumnGroupLayout {...selectedSolution} />
          )}
          {mobileMenuBuilder.styles()}
        </div>
      ) : (
        <div className="solutions">
          <Tabs index={tabIndex} onChange={(index) => setTabAndUrl(index)}>
            <TabList className={tabsStyles.className}>
              {solutions.map((individualSolution, index) => {
                return (
                  <Tab
                    className={tabStyles.className}
                    key={index}
                    style={{
                      borderTopColor: tabBorderColor(individualSolution),
                    }}
                  >
                    <Text size="button-bold" brand="default">
                      {tabHeading(individualSolution)}
                    </Text>
                  </Tab>
                )
              })}
            </TabList>

            <TabPanels>
              {solutions.map((individualSolution, index) => {
                const component = isSolutionWithTiers(individualSolution) ? (
                  <SolutionWithTiers {...individualSolution} />
                ) : (
                  <PlanColumnGroupLayout {...individualSolution} />
                )

                return (
                  <TabPanel className={tabPanelStyles.className} key={index}>
                    {component}
                  </TabPanel>
                )
              })}
            </TabPanels>
          </Tabs>
          {tabsStyles.styles}
          {tabStyles.styles}
          {tabPanelStyles.styles}
        </div>
      )}

      <style jsx>{`
        .solutions-mobile {
          background-color: ${Color.Stone};
          margin-top: 20px;
        }
      `}</style>

      <style jsx>{styles}</style>
    </div>
  )
}
